import React, { useState, useEffect, useCallback } from "react";
import Navigation from "@/components/Navigation";
import {
  Users, PawPrint, Calendar, FileText, DollarSign, TrendingUp,
  HardDrive, ShieldCheck, Search, Loader2,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stable chart-config references (avoid creating new objects on every render).
const ADMIN_CHART_MARGIN = { top: 8, right: 16, bottom: 4, left: -8 };
const ADMIN_AXIS_TICK = { fontSize: 10, fill: "#94A3B8" };
const ADMIN_TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 };
const ADMIN_LINE_DOT = { r: 2, fill: "#FF8A65" };
const ADMIN_LINE_ACTIVE_DOT = { r: 5 };

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${u[i]}`;
}

function fmtCurrency(n) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function StatCard({ icon: Icon, label, value, sub, color = "#FF8A65", bg = "#FFF5E6" }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_15px_rgba(0,0,0,0.04)] p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: bg }}>
          <Icon size={18} style={{ color }} strokeWidth={2.5} />
        </div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p>
      </div>
      <p className="text-3xl font-black text-slate-800 font-['Nunito']">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ total: 0, users: [] });
  const [transactions, setTransactions] = useState({ total: 0, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [tab, setTab] = useState("overview"); // overview | users | transactions

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, t] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: "include" }),
        fetch(`${API}/admin/users?limit=50`, { credentials: "include" }),
        fetch(`${API}/admin/transactions?limit=50`, { credentials: "include" }),
      ]);
      if (s.ok) setStats(await s.json());
      if (u.ok) setUsers(await u.json());
      if (t.ok) setTransactions(await t.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleUserSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    const params = new URLSearchParams();
    if (searchQ) params.set("q", searchQ);
    if (planFilter) params.set("plan", planFilter);
    params.set("limit", "100");
    try {
      const r = await fetch(`${API}/admin/users?${params}`, { credentials: "include" });
      if (r.ok) setUsers(await r.json());
    } finally { setLoading(false); }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen" style={{ background: "#FAF9F6" }}>
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 flex items-center justify-center">
          <Loader2 size={28} className="animate-spin text-[#FF8A65]" />
        </div>
      </div>
    );
  }

  const trendData = (stats?.trend?.signups_30d || []).map(d => ({
    label: new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    count: d.count,
  }));

  return (
    <div className="min-h-screen font-['Figtree']" style={{ background: "#FAF9F6" }} data-testid="admin-dashboard">
      <Navigation />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <header className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#FFF5E6] flex items-center justify-center">
              <ShieldCheck size={22} className="text-[#FF8A65]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800 font-['Nunito']">Admin Dashboard</h1>
              <p className="text-xs text-slate-500">Internal — aggregate metrics &amp; user directory</p>
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-full p-1 w-fit border border-slate-100 shadow-sm">
          {[
            { id: "overview", label: "Overview" },
            { id: "users", label: `Users (${users.total})` },
            { id: "transactions", label: `Transactions (${transactions.total})` },
          ].map(t => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-xs font-semibold rounded-full transition-all ${tab === t.id ? "bg-[#FF8A65] text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <>
            {/* Top stat row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard icon={Users} label="Total Users" value={stats.users.total}
                sub={`+${stats.users.new_7d} this week · +${stats.users.new_30d} this month`} />
              <StatCard icon={DollarSign} label="MRR" value={fmtCurrency(stats.revenue.mrr_usd)}
                sub={`ARR ≈ ${fmtCurrency(stats.revenue.arr_usd)}`} color="#388E3C" bg="#E8F5E9" />
              <StatCard icon={TrendingUp} label="Revenue (30d)" value={fmtCurrency(stats.revenue.revenue_30d_usd)}
                sub={`${stats.revenue.paid_transactions_30d} paid txns`} color="#1565C0" bg="#E3F2FD" />
              <StatCard icon={HardDrive} label="Storage Used" value={fmtBytes(stats.storage.total_bytes)}
                sub={`${fmtBytes(stats.storage.photo_bytes)} photos · ${fmtBytes(stats.storage.record_bytes)} records`}
                color="#6A1B9A" bg="#F3E5F5" />
            </div>

            {/* Plan breakdown + activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_15px_rgba(0,0,0,0.04)] p-6 lg:col-span-1">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Plan breakdown</h3>
                <div className="space-y-3">
                  {[
                    { label: "Free", value: stats.users.free, color: "#94A3B8" },
                    { label: "Premium · monthly", value: stats.users.premium_monthly, color: "#FF8A65" },
                    { label: "Premium · annual", value: stats.users.premium_annual, color: "#388E3C" },
                  ].map(row => {
                    const pct = stats.users.total ? Math.round((row.value / stats.users.total) * 100) : 0;
                    return (
                      <div key={row.label}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-slate-700 font-medium">{row.label}</span>
                          <span className="font-bold text-slate-800">{row.value} <span className="text-xs text-slate-400">({pct}%)</span></span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: row.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs text-slate-500">Active sessions (7d)</p>
                  <p className="text-2xl font-extrabold text-slate-800 font-['Nunito']">{stats.users.active_sessions_7d}</p>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_15px_rgba(0,0,0,0.04)] p-6 lg:col-span-2">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">New signups · last 30 days</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={ADMIN_CHART_MARGIN}>
                      <CartesianGrid vertical={false} stroke="#F1F5F9" />
                      <XAxis dataKey="label" tick={ADMIN_AXIS_TICK} axisLine={false} tickLine={false}
                        interval={Math.max(0, Math.floor(trendData.length / 8))} />
                      <YAxis tick={ADMIN_AXIS_TICK} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                      <Tooltip contentStyle={ADMIN_TOOLTIP_STYLE} />
                      <Line type="monotone" dataKey="count" stroke="#FF8A65" strokeWidth={2.5}
                        dot={ADMIN_LINE_DOT} activeDot={ADMIN_LINE_ACTIVE_DOT} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Data row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={PawPrint} label="Total Pets" value={stats.data.total_pets}
                sub={`avg ${stats.data.avg_pets_per_user} per user`} />
              <StatCard icon={Calendar} label="Reminders" value={stats.data.reminders_total}
                sub={`${stats.data.reminders_active} active · ${stats.data.reminders_overdue} overdue`}
                color="#388E3C" bg="#E8F5E9" />
              <StatCard icon={FileText} label="Vet Records" value={stats.data.vet_records_total}
                color="#1565C0" bg="#E3F2FD" />
              <StatCard icon={TrendingUp} label="Reminders done" value={stats.data.reminders_completed}
                color="#6A1B9A" bg="#F3E5F5" />
            </div>
          </>
        )}

        {tab === "users" && (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_15px_rgba(0,0,0,0.04)] p-6">
            <form onSubmit={handleUserSearch} className="flex flex-col sm:flex-row gap-2 mb-5">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="admin-user-search"
                  value={searchQ}
                  onChange={e => setSearchQ(e.target.value)}
                  placeholder="Search email or name…"
                  className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]"
                />
              </div>
              <select
                data-testid="admin-user-plan-filter"
                value={planFilter}
                onChange={e => setPlanFilter(e.target.value)}
                className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]"
              >
                <option value="">All plans</option>
                <option value="free">Free</option>
                <option value="premium">Premium</option>
              </select>
              <button type="submit" className="px-5 py-2.5 rounded-xl bg-[#FF8A65] text-white text-sm font-semibold hover:bg-[#FF7043] transition-colors">
                Search
              </button>
            </form>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="text-left py-3 pr-4 font-semibold">Email</th>
                    <th className="text-left py-3 pr-4 font-semibold">Name</th>
                    <th className="text-left py-3 pr-4 font-semibold">Plan</th>
                    <th className="text-left py-3 pr-4 font-semibold">Auth</th>
                    <th className="text-right py-3 pr-4 font-semibold">Pets</th>
                    <th className="text-right py-3 pr-4 font-semibold">Reminders</th>
                    <th className="text-left py-3 pr-4 font-semibold">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.users.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-slate-400">No users match these filters.</td></tr>
                  )}
                  {users.users.map(u => (
                    <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 pr-4 text-slate-700 font-medium">
                        {u.email}
                        {u.is_admin && <span className="ml-2 text-[10px] font-bold uppercase bg-[#FFF5E6] text-[#FF8A65] px-2 py-0.5 rounded-full">admin</span>}
                      </td>
                      <td className="py-3 pr-4 text-slate-600">{u.name || "—"}</td>
                      <td className="py-3 pr-4">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${u.plan === "premium" ? "bg-[#E8F5E9] text-[#388E3C]" : "bg-slate-100 text-slate-500"}`}>
                          {u.plan === "premium" ? `Premium · ${u.plan_type || "monthly"}` : "Free"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-xs text-slate-500">{u.auth_provider || "—"}</td>
                      <td className="py-3 pr-4 text-right tabular-nums text-slate-700">{u.pet_count}</td>
                      <td className="py-3 pr-4 text-right tabular-nums text-slate-700">{u.reminder_count}</td>
                      <td className="py-3 pr-4 text-xs text-slate-500">{fmtDate(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Showing {users.users.length} of {users.total} users.
              Admin sees user metadata only — pet contents, photos, vet records, and medical notes are never accessible from this view.
            </p>
          </div>
        )}

        {tab === "transactions" && (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_15px_rgba(0,0,0,0.04)] p-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="text-left py-3 pr-4 font-semibold">Date</th>
                    <th className="text-left py-3 pr-4 font-semibold">User ID</th>
                    <th className="text-left py-3 pr-4 font-semibold">Plan</th>
                    <th className="text-right py-3 pr-4 font-semibold">Amount</th>
                    <th className="text-left py-3 pr-4 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.transactions.length === 0 && (
                    <tr><td colSpan={5} className="py-8 text-center text-slate-400">No transactions yet.</td></tr>
                  )}
                  {transactions.transactions.map(t => (
                    <tr key={t.transaction_id} className="border-b border-slate-50">
                      <td className="py-3 pr-4 text-xs text-slate-500">{fmtDate(t.created_at)}</td>
                      <td className="py-3 pr-4 text-xs font-mono text-slate-600">{t.user_id}</td>
                      <td className="py-3 pr-4 text-xs text-slate-600">{t.plan_type || "—"}</td>
                      <td className="py-3 pr-4 text-right font-semibold text-slate-800 tabular-nums">
                        {fmtCurrency(t.amount || 0)}
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          t.payment_status === "paid" ? "bg-[#E8F5E9] text-[#388E3C]" :
                          t.payment_status === "unpaid" ? "bg-slate-100 text-slate-500" :
                          "bg-red-100 text-red-600"
                        }`}>
                          {t.payment_status || t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
